<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("customer_list") }}</h4>
        </div>
      </div>
      <div class="page-btn d-flex gap-2 item-io-btns">
        <button
          v-if="can('sales.customer', 'R')"
          type="button"
          class="btn btn-item-export"
          :disabled="exporting"
          @click="exportCustomers"
        >
          <span v-if="exporting" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="ti ti-database-export me-1"></i>{{ $t("export_customer") }}
        </button>
        <router-link v-if="can('sales.customer', 'W')" to="/customers/import" class="btn btn-item-import">
          <i class="ti ti-database-import me-1"></i>{{ $t("import_customer") }}
        </router-link>
        <router-link v-if="can('sales.customer', 'W')" to="/customers/create" class="btn btn-added">
          <i class="ti ti-circle-plus me-2 text-white"></i>{{ $t("add_new_customer") }}
        </router-link>
      </div>
    </div>

    <ThemeDataTable
      ref="customerTable"
      table-id="CustomerListTable"
      :fetch-data="fetchCustomersPage"
      :filters="tableFilters"
    >
      <template #default="{ rows }">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("customer_name") }}</th>
            <th>{{ $t("customer_code") }}</th>
            <th>{{ $t("due_limit") }}</th>
            <th>{{ $t("due_limit_days") }}</th>
            <th>{{ $t("customer_address") }}</th>
            <th>{{ $t("customer_bank") }}</th>
            <th>{{ $t("customer_contact_person") }}</th>
            <th>{{ $t("customer_email") }}</th>
            <th>{{ $t("customer_phone") }}</th>
            <th>{{ $t("status") }}</th>
            <th class="no-sort text-end">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in rows" :key="customer.id">
            <td>{{ customer.customer_name }}</td>
            <td>{{ customer.customer_code }}</td>
            <td>{{ customer.due_limit }}</td>
            <td>{{ customer.due_limit_days }}</td>
            <td>{{ customer.customer_address }}</td>
            <td>{{ customer.customer_bank }}</td>
            <td>{{ customer.customer_contact_person }}</td>
            <td>{{ customer.customer_email }}</td>
            <td>{{ customer.customer_phone }}</td>
            <td>{{ customer.status }}</td>
            <td class="action-table-data text-end">
              <div v-if="can('sales.customer', 'E')" class="edit-delete-action justify-content-end">
                <router-link
                  :to="{ name: 'CustomerEdit', params: { id: customer.id } }"
                  class="me-2 p-2"
                  :title="$t('edit_customer')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ti ti-edit">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </router-link>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </ThemeDataTable>
  </div>
</template>

<script>
import { can as canAccess } from "@/helpers/permission";
import apiUrls from '@/urls';
import ThemeDataTable from '@/components/common/ThemeDataTable.vue';

export default {
  components: {
    ThemeDataTable,
  },
  data() {
    return {
      exporting: false,
      filterOptions: {
        status: ['enabled', 'disabled'],
      },
    };
  },
  computed: {
    tableFilters() {
      return [
        {
          key: 'status',
          label: this.$t('status'),
          options: this.filterOptions.status,
        },
      ];
    },
  },
  methods: {
    async fetchCustomersPage({ draw, start, length, search, orderColumn, orderDir, filters }) {
      const params = {
        draw,
        start,
        length,
        'search[value]': search || '',
        'order[0][column]': orderColumn,
        'order[0][dir]': orderDir,
        ...filters,
      };

      const response = await this.$axios.get(apiUrls.customers.get, { params });
      const payload = response.data || {};

      return {
        data: payload.data || [],
        recordsTotal: payload.recordsTotal || 0,
        recordsFiltered: payload.recordsFiltered || 0,
      };
    },

    can(code, requiredLevel) {
      return canAccess(code, requiredLevel);
    },

    exportFilename(response) {
      const header = response?.headers?.['content-disposition'] || '';
      const star = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
      const plain = /(?:^|;)\s*filename="?([^";]+)"?/i.exec(header);
      let name = (star && star[1]) || (plain && plain[1]) || '';
      try {
        name = decodeURIComponent(name.trim().replace(/^"|"$/g, ''));
      } catch (e) {
        name = name.trim().replace(/^"|"$/g, '');
      }
      if (!name.toLowerCase().endsWith('.xlsx')) {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        name = `Customer_exported_at_${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}.xlsx`;
      }
      return name;
    },

    async exportCustomers() {
      this.exporting = true;
      try {
        const response = await this.$axios.get(apiUrls.customers.export, { responseType: 'blob' });
        const contentType = (response.headers['content-type'] || '').toLowerCase();
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const detail = JSON.parse(text);
          throw new Error(detail.error || detail.detail || 'Export failed');
        }
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.exportFilename(response);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        window.alert(error?.message || 'Customer export failed.');
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
:deep(table .edit-delete-action.justify-content-end) {
  justify-content: flex-end !important;
}
:deep(td.action-table-data.text-end) {
  text-align: right !important;
}
.item-io-btns .btn {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  border-radius: 8px;
}
.btn-item-export {
  background: #ecfdf5;
  border: 1px solid #10b981;
  color: #047857;
}
.btn-item-import {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  color: #1d4ed8;
}
</style>
